import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Upload, Image as ImageIcon, Search } from 'lucide-react';
import { listMedia } from '../lib/supabase';

interface Props {
  onSelect: (url: string) => void;
  onUpload: (file: File) => Promise<void> | void;
  onClose: () => void;
  uploading?: boolean;
  title?: string;
}

/**
 * Modal zum Auswählen eines Bildes aus der Mediathek oder neu hochladen.
 */
export function MediaPicker({ onSelect, onUpload, onClose, uploading, title = 'Bild auswählen' }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['media'], queryFn: () => listMedia() });

  const filtered = (data ?? []).filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return m.filename.toLowerCase().includes(q) || (m.alt_text?.toLowerCase().includes(q) ?? false);
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: '1rem',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <div style={{
        background: 'var(--surface-primary)',
        borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)',
        width: '100%', maxWidth: 900, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'scaleIn 0.2s ease',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.125rem 1.5rem', borderBottom: '1px solid var(--border-color)',
        }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-light)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-input-wrapper" style={{ flex: 1, minWidth: 240 }}>
            <Search size={16} />
            <input className="search-input" placeholder="Bilder suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <Upload size={14} /> {uploading ? 'Lade hoch...' : 'Neu hochladen'}
          </button>
        </div>

        <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <ImageIcon size={48} />
              <h3>{search ? 'Keine Treffer' : 'Noch keine Bilder'}</h3>
              <p>{search ? 'Andere Suche versuchen.' : 'Lade oben rechts dein erstes Bild hoch.'}</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '0.75rem',
            }}>
              {filtered.map(m => (
                <button
                  key={m.id}
                  onClick={() => onSelect(m.url)}
                  style={{
                    background: 'var(--surface-primary)',
                    border: '2px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    padding: 0,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ width: '100%', aspectRatio: '1', background: 'var(--surface-secondary)', overflow: 'hidden' }}>
                    <img src={m.url} alt={m.alt_text ?? m.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div style={{ padding: '0.375rem 0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.filename}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
